import React, { createContext, useContext, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setShowFreeSpinOutro,
  setShowFreeSpinRetrigger,
  setShowFreeSpinTransition,
  setFreeSpinOutroTriggered,
  setUserInterfaceDisabled,
  closeAllPopups,
  stopAutoPlay,
} from "../State/actions";
import { selectAnyPopupActive } from "../State/selectors";

export const FreeSpinAnimationContext = createContext(null);

export const FreeSpinAnimationProvider = ({ children }) => {
  const dispatch = useDispatch();

  // Track which rounds have already shown retrigger dialog
  const retriggerShownRef = useRef(new Set());

  // Store reel decoration controller ref for outro to control
  const reelDecorationControllerRef = useRef(null);

  // Main game selectors
  const { currentRound } = useSelector((state) => state.game);
  const { spinComplete, isSpinning } = useSelector((state) => state.spin);
  const { linesRotationFinished } = useSelector((state) => state.animations);
  const { autoPlayActive } = useSelector((state) => state.settings);
  const anyPopupActive = useSelector(selectAnyPopupActive);

  // Free spin selectors
  const {
    freeSpinsActive,
    currentFreeSpinRound,
    symbolsRevealed,
    currentFreeSpinRoundIndex,
  } = useSelector((state) => state.freeSpins);

  const {
    freeSpinLinesRotationFinished,
    freeSpinWinCounterFinished,
    freeSpinOutroTriggered,
  } = useSelector((state) => state.freeSpinAnimations);
  const { winAnimationsPlaying } = useSelector((state) => state.animations);

  // Reset retrigger tracking and outro triggered flag when round changes
  useEffect(() => {
    if (currentFreeSpinRoundIndex !== undefined) {
      retriggerShownRef.current.clear();
      dispatch(setFreeSpinOutroTriggered(false)); // Reset outro trigger flag
    }
  }, [currentFreeSpinRoundIndex, dispatch]);

  // Handle all free spin animation triggers
  useEffect(() => {
    // Feature trigger in main game - disable UI and show transition
    if (
      currentRound?.type === "feature" &&
      spinComplete &&
      !isSpinning &&
      !freeSpinsActive
    ) {
      dispatch(setUserInterfaceDisabled(true));

      // Close any open popups before starting free spin transition
      if (anyPopupActive) {
        dispatch(closeAllPopups());
      }

      if (autoPlayActive) {
        dispatch(stopAutoPlay());
      }

      // Show deer transition after UI disabled
      if (linesRotationFinished) {
        requestAnimationFrame(() => {
          dispatch(setShowFreeSpinTransition(true));
        });
      }
    }

    // Feature trigger during free spins - show retrigger dialog
    if (
      freeSpinsActive &&
      currentFreeSpinRound?.type === "feature" &&
      freeSpinLinesRotationFinished &&
      symbolsRevealed
    ) {
      // Create unique round identifier to prevent duplicate dialogs
      const roundId =
        currentFreeSpinRound?.id || `round-${currentFreeSpinRoundIndex}`;

      // Only show retrigger dialog once per round
      if (!retriggerShownRef.current.has(roundId)) {
        retriggerShownRef.current.add(roundId);
        dispatch(setUserInterfaceDisabled(true));
        dispatch(setShowFreeSpinRetrigger(true));
      }
    }

    // Free spins completion - show outro dialog (only once per round)
    if (
      freeSpinsActive &&
      currentFreeSpinRound?.freeSpins === 0 &&
      symbolsRevealed &&
      !freeSpinOutroTriggered // Prevent duplicate triggers
    ) {
      // Check if round has wins
      const hasWins = currentFreeSpinRound?.win && currentFreeSpinRound.win > 0;

      if (
        hasWins &&
        freeSpinLinesRotationFinished &&
        freeSpinWinCounterFinished &&
        !winAnimationsPlaying
      ) {
        // Has wins: Wait for win animations to complete, then trigger outro
        dispatch(setFreeSpinOutroTriggered(true));
        dispatch(setShowFreeSpinOutro(true));
      } else if (!hasWins) {
        // No wins: Wait 1000ms then trigger outro
        const timer = setTimeout(() => {
          dispatch(setFreeSpinOutroTriggered(true));
          dispatch(setShowFreeSpinOutro(true));
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [
    spinComplete,
    isSpinning,
    currentRound?.type,
    dispatch,
    freeSpinsActive,
    autoPlayActive,
    linesRotationFinished,
    anyPopupActive,
    currentFreeSpinRound?.type,
    currentFreeSpinRound?.freeSpins,
    currentFreeSpinRound?.win,
    currentFreeSpinRound?.id,
    currentFreeSpinRoundIndex,
    symbolsRevealed,
    freeSpinLinesRotationFinished,
    freeSpinWinCounterFinished,
    freeSpinOutroTriggered,
    winAnimationsPlaying,
  ]);

  return (
    <FreeSpinAnimationContext.Provider value={{ reelDecorationControllerRef }}>
      {children}
    </FreeSpinAnimationContext.Provider>
  );
};

// Custom hook
export const useFreeSpinAnimation = () => {
  const context = useContext(FreeSpinAnimationContext);
  if (!context) {
    throw new Error(
      "useFreeSpinAnimation must be used within a FreeSpinAnimationProvider"
    );
  }
  return context;
};
